import { useRef, useState } from "react";
import {
  Button,
  DataGrid,
  FormControl,
  Grid,
  TextField,
  Autocomplete,
  CircularProgress,
  Tooltip,
  Typography,
  CurrencyInput,
  TextareaAutosize,
} from "../../../../components";
import { formatPrice, responseErros, unformatPrice } from "../../../../utils";
import { value } from "../../../../helpers";
import {
  NfseService,
  ServicoService,
  notification,
} from "../../../../services";
import { nfseServicoValidator } from "../middlewares";
import ImpostoServicoDialog from "./dialogos/imposto-servico-dialog";

const servicoService = new ServicoService();
const nfseService = new NfseService();

const ListaServicosNfse = ({
  servicos,
  setServicos,
  id,
  buscarNfse,
  formularioDesabilitado,
  nfse,
  setNfse,
  setLoading,
  inputErros,
}) => {
  const inputServico = useRef();
  const [servicoList, setServicoList] = useState([]);
  const [servico, setServico] = useState(null);
  const [editandoServico, setEditandoServico] = useState(false);
  const [loadingAutoCompleteServico, setLoadingAutoCompleteServico] =
    useState(false);
  const [openImpostoDialog, setOpenImpostoDialog] = useState(false);
  const [servicoImposto, setServicoImposto] = useState(null);
  const colunas = [
    {
      field: "id",
      headerName: "Código",
      flex: 100,
    },
    {
      field: "descricao",
      headerName: "Serviço",
      flex: 500,
    },
    {
      field: "quantidade",
      headerName: "Qtd.",
      flex: 90,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "valor",
      headerName: "Valor (R$)",
      flex: 200,
      headerAlign: "right",
      align: "right",
      valueGetter: (params) => formatPrice(parseFloat(params.value || 0)),
    },
    {
      field: "totalItem",
      headerName: "Total (R$)",
      flex: 180,
      headerAlign: "right",
      align: "right",
      valueGetter: (params) => {
        return formatPrice(totalServicos(params.row));
      },
    },
  ];

  const totalServicos = (servico) => {
    return (servico?.quantidade || 0) * (servico?.valor || 0);
  };

  const onInputChangeServicos = (event) => {
    if (
      event.key === "Enter" &&
      inputServico.current === document.activeElement
    ) {
      const eventValue = event.target?.value;
      if (eventValue && eventValue.trim().length > 0) {
        setLoadingAutoCompleteServico(true);
        buscarServicos(eventValue);
      }
    }
  };

  const buscarServicos = async (termoBusca) => {
    const filtros = {
      nome: termoBusca,
      descricao: termoBusca,
      codigo: termoBusca,
      ativado: true,
    };

    try {
      const response = await servicoService.getAll(filtros);
      setLoadingAutoCompleteServico(false);
      if (!response.isAxiosError && response.data) {
        setServicoList(response.data.rows);
      } else {
        responseErros(response);
        setServicoList([]);
      }
    } catch (error) {
      setLoadingAutoCompleteServico(false);
      setServicoList([]);
      console.error("Erro ao buscar serviços:", error);
    }
  };

  const handleChangeServico = (value) => {
    if (!value) return setServico(null);
    const novoServico = {
      servicoId: value.id,
      codigo: value.id,
      descricao: value.descricao,
      quantidade: 1,
      valor: value.precoServico || 0,
      totalItem: value.precoServico || 0,
      classificacaoServicoNacional:
        value?.classificacaoServicoNacional?.codigoClassificacao,
      codigoServicoNacional: value?.codigoServicoNacional?.codigoNacional,
      aliquotaTotalSimplesNacional: value?.aliquotaTotalSimplesNacional,
      codigoServicoMunicipal: value?.codigoServicoMunicipal,
      grupoTributarioId: value?.grupoTributarioId,
    };
    setServico(novoServico);
  };

  const handleAdicionarServico = (servicoParaAdicionar) => {
    nfseServicoValidator
      .validate(servicoParaAdicionar, { abortEarly: false })
      .then(async () => {
        if (editandoServico) {
          atualizarServico(servicoParaAdicionar);
        } else {
          inserirServico(servicoParaAdicionar);
        }
      })
      .catch((err) => {
        inputErros.set(err);
      });
    setTimeout(() => {
      if (inputServico?.current) {
        inputServico.current.focus();
      }
    }, 100);
  };

  const inserirServico = (servicoParaAdicionar) => {
    const servicoExistente = servicos.find(
      (s) => s.servicoId === servicoParaAdicionar.servicoId,
    );
    if (servicoExistente) {
      notification.alertaGenericos("Este serviço já foi adicionado.");
      setServico(null);
      return;
    }
    if (id) {
      cadastrarServico(servicoParaAdicionar);
    } else {
      adicionarServico(servicoParaAdicionar);
    }
  };

  const atualizarServico = async (servicoParaAdicionar) => {
    if (!id) {
      adicionarServicoLista(servicoParaAdicionar);
      return;
    }
    atualizarServicoBanco(servicoParaAdicionar);
  };

  const atualizarServicoBanco = async (servicoParaAdicionar) => {
    setLoading(true);
    const response = await nfseService.atualizarServico(
      id,
      servicoParaAdicionar.id,
      servicoParaAdicionar,
    );
    setLoading(false);
    if (!response.isAxiosError) {
      notification.alteracaoSucesso();
      setServico(null);
      setEditandoServico(false);
      setServicoList([]);
      await buscarNfse();
    } else {
      responseErros(response);
    }
  };

  const adicionarServicoLista = (servicoParaAdicionar) => {
    const servicosAtualizados = servicos.map((servico) => {
      if (servico.servicoId === servicoParaAdicionar.servicoId) {
        return {
          ...servico,
          quantidade: servicoParaAdicionar.quantidade,
          valor: servicoParaAdicionar.valor,
        };
      }
      return servico;
    });
    setServicos(servicosAtualizados);
    setEditandoServico(false);
    setServico(null);
    setServicoList([]);
  };

  const cadastrarServico = async (servicoParaAdicionar) => {
    setLoading(true);
    const response = await nfseService.cadastrarServico(
      id,
      servicoParaAdicionar,
    );
    setLoading(false);
    if (!response.isAxiosError) {
      notification.cadastroSucesso();
      setServico(null);
      await buscarNfse();
    } else {
      responseErros(response);
    }
  };

  function adicionarServico(servicoParaAdicionar) {
    const servicosAtualizados = [
      ...servicos,
      {
        ...servicoParaAdicionar,
        id: Date.now().toString(),
      },
    ];
    setServicos(servicosAtualizados);
    atualizarDescricaoServico(servicosAtualizados);
    setServico(null);
  }

  function atualizarDescricaoServico(servicosAtualizados) {
    setNfse((prevNfse) => ({
      ...prevNfse,
      descricaoServico: servicosAtualizados
        .map((servico) => servico.descricao)
        .join("; "),
    }));
  }

  const handleInputChange = (event) => {
    const { name, value: inputValue } = event.target;
    const servicoAtualizado = {
      ...servico,
      [name]: inputValue,
    };
    servicoAtualizado.totalItem =
      (servicoAtualizado.quantidade || 0) * (servicoAtualizado.valor || 0);
    setServico(servicoAtualizado);
  };

  const handleInputChangeNfse = (event) => {
    const { name, value: inputValue } = event.target;
    const nfseAtualizado = {
      ...nfse,
      [name]: inputValue,
    };
    setNfse(nfseAtualizado);
  };

  const handleInputBlur = (event) => {
    const { name, value } = event.target;
    const valorInserido = parseFloat(unformatPrice(value));
    const servicoAtualizado = {
      ...servico,
      [name]: valorInserido,
    };
    servicoAtualizado.totalItem =
      (servicoAtualizado.quantidade || 0) * (servicoAtualizado.valor || 0);
    setServico(servicoAtualizado);
  };

  const onClickDelete = (idServico) => {
    if (id) {
      if (servicos.length == 1) {
        notification.alertaGenericos("A NFSe deve conter ao menos um serviço.");
        return;
      }
      deletarServico(idServico);
      return;
    }
    removerServicoLista(idServico);
  };

  const deletarServico = async (idServico) => {
    const response = await nfseService.deletarServico(id, idServico);
    if (!response.isAxiosError) {
      notification.deletadoSucesso();
      await buscarNfse();
      return;
    }
    responseErros(response);
  };

  function removerServicoLista(id) {
    const novaLista = servicos.filter((s) => s.id !== id);
    atualizarDescricaoServico(novaLista);
    setServicos(novaLista);
  }

  const handleViewImposto = async (idServico) => {
    const servicoSelecionado = servicos.find((s) => s.id === idServico);
    if (!servicoSelecionado) {
      return notification.alertaGenericos("Serviço não encontrado");
    }
    setServicoImposto({ ...servicoSelecionado });
    setOpenImpostoDialog(true);
  };

  const onClickEdit = (idServico) => {
    const servicoSelecionado = servicos.find((s) => s.id === idServico);
    if (servicoSelecionado) {
      const servicoParaEdicao = {
        ...servicoSelecionado,
        codigo: servicoSelecionado.id,
      };
      setServico(servicoParaEdicao);
      setServicoList([servicoParaEdicao]);
      setEditandoServico(true);
    }
  };

  const autoCompleteServicoDesabilitado = () => {
    const desabilitado = formularioDesabilitado();
    return desabilitado || editandoServico;
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={5}>
        <FormControl variant="outlined" margin="normal" fullWidth>
          <Autocomplete
            id="servicoId"
            name="servicoId"
            options={servicoList}
            autoHighlight
            loading={loadingAutoCompleteServico}
            loadingText="Carregando"
            disabled={autoCompleteServicoDesabilitado()}
            noOptionsText="Digite e pressione Enter para buscar"
            renderOption={(props, option) => {
              let nome = `${option?.codigo} - ${option?.nome}`;
              return option ? (
                <li {...props} style={{ background: "transparent" }}>
                  <div className="d-flex justify-content-between align-items-center w-100">
                    <div>
                      <span className="fw-bold">{nome}</span>
                      <Typography variant="body2">
                        Descrição: <span>{option?.descricao}</span>
                      </Typography>
                    </div>
                    <div className="text-end">
                      <Typography variant="body2">Preço serviço</Typography>
                      <span className="fs-6">
                        {formatPrice(option?.precoServico || 0)}
                      </span>
                    </div>
                  </div>
                </li>
              ) : (
                ""
              );
            }}
            getOptionLabel={(option) => {
              let descricaoServico = `${option?.codigo} - ${option?.descricao}`;
              return option ? descricaoServico : "";
            }}
            filterSelectedOptions
            onChange={(_, valorSelecionado) =>
              handleChangeServico(valorSelecionado)
            }
            value={servico}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Buscar serviço"
                required
                onKeyDown={(e) => onInputChangeServicos(e)}
                inputRef={inputServico}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingAutoCompleteServico ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </FormControl>
      </Grid>
      <Grid item xs={2}>
        <TextField
          id="quantidade"
          name="quantidade"
          label="Quantidade"
          variant={!servico ? "filled" : "outlined"}
          error={inputErros.get("quantidade")}
          disabled={!servico}
          InputProps={{ inputProps: { min: 1, step: 1 } }}
          value={value.text(servico?.quantidade)}
          onChange={handleInputChange}
          margin="normal"
          type="number"
        />
      </Grid>
      <Grid item xs={2}>
        <CurrencyInput
          id="valor"
          name="valor"
          label="Valor Serviço R$"
          error={inputErros.get("valor")}
          variant={!servico ? "filled" : "outlined"}
          disabled={!servico}
          value={value.text(servico?.valor)}
          onBlur={handleInputBlur}
        />
      </Grid>
      <Grid item xs={2}>
        <CurrencyInput
          id="totalItem"
          name="totalItem"
          label="Total Item R$"
          variant="filled"
          disabled
          value={value.text(servico?.totalItem)}
        />
      </Grid>
      <Grid item xs={0.5} className="mt-4">
        <Tooltip title={"Adicionar Serviço"}>
          <Button
            id="buttonAdicionarServico"
            variant="contained"
            color={"success"}
            size="small"
            disabled={!servico}
            sx={{
              height: "35px",
              borderRadius: "50%",
              minWidth: "15px",
              boxShadow: "none",
            }}
            onClick={() => handleAdicionarServico(servico)}
          >
            <i
              className={"ph-bold ph-plus"}
              style={{ color: "#fff", fontSize: 15 }}
            ></i>
          </Button>
        </Tooltip>
      </Grid>
      <Grid item xs={12}>
        <DataGrid
          rows={servicos}
          rowCount={servicos.length}
          columns={colunas}
          deleteShow={!formularioDesabilitado()}
          editShow={!formularioDesabilitado()}
          onClickDelete={onClickDelete}
          onClickEdit={onClickEdit}
          imposto={id}
          onClickViewImposto={handleViewImposto}
        />
        <ImpostoServicoDialog
          open={openImpostoDialog}
          setOpen={setOpenImpostoDialog}
          servico={servicoImposto}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography className="text-start">Descrição do serviço</Typography>
        <TextareaAutosize
          className="textRolagem"
          id="descricaoServico"
          name="descricaoServico"
          label="Descrição do serviço"
          variant="outlined"
          maxLength={255}
          disabled={formularioDesabilitado()}
          value={nfse?.descricaoServico ?? ""}
          onChange={handleInputChangeNfse}
        />
      </Grid>
    </Grid>
  );
};

export default ListaServicosNfse;
